import { COLORS } from "../core/constants.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";
import { UI_FONT_STACK, UI_EASE, withUiScale } from "./uiTokens.ts";

/** The read-only slice of ScoreSystem the readout needs. ScoreSystem satisfies
 *  this structurally, so the UI never imports a systems module. */
export interface ComboReadoutSource {
  readonly comboMultiplier: number;
  comboWindowRemaining(): number;
}

const STYLE_ID = "combo-readout-styles";
/** One-shot celebratory aura-sweep duration (the wave-clear flourish). */
const FLOURISH_MS = 600;

/**
 * BL-209 (S189) — the combo made VISIBLE ON THE BOARD (not just the HUD number).
 *
 * A board-level, pointer-events-none overlay that reads ScoreSystem each frame:
 *  - a screen-edge AURA (inset glow) whose intensity scales with the combo
 *    multiplier (x1 → off, x10 → full), so a hot chain reads as a power-surge;
 *  - a pulsing "×N" chip near the top of the board;
 *  - the whole thing fades as the combo window closes (comboWindowRemaining()).
 *
 * Render-only: it only READS the score system (comboMultiplier getter +
 * comboWindowRemaining(), both pure) — never mutates sim/RNG, so golden masters
 * stay byte-identical. Reduced-motion: the chip pulse is gated (static "×N" +
 * glow + aura still convey the multiplier — information preserved). The aura is
 * a steady, slowly-fading glow (never a strobe), so it is photosensitivity-safe
 * without routing through the FlashGovernor.
 */
export class ComboReadout {
  private readonly root: HTMLDivElement;
  private readonly chip: HTMLDivElement;
  private readonly source: ComboReadoutSource;
  private raf = 0;
  /** Render-clock deadline for the one-shot wave-clear flourish sweep. */
  private flourishUntil = 0;

  constructor(parent: HTMLElement, source: ComboReadoutSource) {
    this.source = source;
    this.injectStyles();

    this.root = document.createElement("div");
    this.root.className = "combo-readout";
    this.root.setAttribute("aria-hidden", "true");
    // BL-215 fix — inert until a run starts (see start()/stop()). Keeps the
    // boot / start / pre-run phases overlay-free, so this never adds to the
    // iPhone first-launch jetsam-reload pressure.
    this.root.style.display = "none";

    const aura = document.createElement("div");
    aura.className = "combo-aura";
    this.root.appendChild(aura);

    this.chip = document.createElement("div");
    this.chip.className = "combo-chip";
    this.root.appendChild(this.chip);

    parent.appendChild(this.root);
  }

  /** Begin the per-frame read loop, mounted for the duration of a run. No-op in
   *  headless. */
  start(): void {
    this.root.style.display = "";
    if (this.raf || typeof requestAnimationFrame === "undefined") return;
    const loop = (): void => {
      this.apply(this.source.comboMultiplier, this.source.comboWindowRemaining());
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop(): void {
    if (this.raf && typeof cancelAnimationFrame !== "undefined") {
      cancelAnimationFrame(this.raf);
    }
    this.raf = 0;
    // Drop the overlay out of compositing entirely between runs.
    this.root.style.display = "none";
  }

  destroy(): void {
    this.stop();
    this.root.remove();
  }

  /**
   * BL-210 (S189) — fire a one-shot celebratory aura sweep (the wave-clear
   * "combo-meter flourish"). Reduced-motion-gated inside apply(), so it is
   * absent (not just static) when reduced motion is on.
   */
  flourish(): void {
    this.flourishUntil = Date.now() + FLOURISH_MS;
  }

  /**
   * Pure render step — exposed for tests. `mult` is the live combo multiplier
   * (1,2,3,4,5,7,10); `remaining` is comboWindowRemaining() (0..1).
   */
  apply(mult: number, remaining: number): void {
    const active = mult > 1 && remaining > 0;
    const now = Date.now();
    const flourishT =
      this.flourishUntil > now ? (this.flourishUntil - now) / FLOURISH_MS : 0;
    // A flourish is a one-shot celebratory pulse → gate it like any other, so
    // reduced motion stays static (no sweep at all).
    const flourishing = flourishT > 0 && !isReducedMotion();

    if (!active && !flourishing) {
      this.root.style.opacity = "0";
      this.chip.style.opacity = "0";
      this.chip.classList.remove("is-pulsing");
      return;
    }

    // x2 → ~0.11 … x10 → 1.0; the aura/glow read scales with the chain.
    let intensity = active ? Math.min(Math.max((mult - 1) / 9, 0), 1) : 0;
    // Fade the whole readout as the window closes (the chain "cooling").
    let opacity = active ? 0.35 + 0.65 * remaining : 0;
    if (flourishing) {
      // Bright at the start of the sweep, fading out over FLOURISH_MS.
      intensity = Math.max(intensity, flourishT);
      opacity = Math.max(opacity, 0.55 * flourishT);
    }
    this.root.style.opacity = opacity.toFixed(3);
    this.root.style.setProperty("--combo-i", intensity.toFixed(3));

    if (active) {
      const label = `×${mult}`;
      if (this.chip.textContent !== label) this.chip.textContent = label;
      this.chip.style.opacity = "1";
      // Pulse only when motion is allowed; the static chip still reads the value.
      this.chip.classList.toggle("is-pulsing", !isReducedMotion());
    } else {
      // Pure flourish (no live chain): aura sweep only — no stale number/pulse.
      this.chip.style.opacity = "0";
      this.chip.classList.remove("is-pulsing");
    }
  }

  private injectStyles(): void {
    if (typeof document === "undefined") return;
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      .combo-readout {
        position: absolute;
        inset: 0;
        /* BL-209 fix — this overlay is a permanent full-stage child of
           #ui-layer, whose \`#ui-layer > * { pointer-events: auto }\` (an ID
           rule, index.html) would otherwise WIN over a class \`pointer-events:
           none\` and make this layer swallow every board tap/click. It is purely
           decorative (never interactive), so force it off and let children
           inherit. */
        pointer-events: none !important;
        z-index: 8;
        opacity: 0;
        transition: opacity 180ms ${UI_EASE.glide};
      }
      /* Edge-only inset glow — intensity tracks --combo-i, capped at 0.5 so the
         centre board (towers/enemies/path) is never hidden. */
      /* BL-215 fix — a cheap radial-gradient edge vignette instead of a
         full-stage 72px-blur inset box-shadow (which forced an expensive
         promoted blur layer that pressured the iPhone GPU/jetsam budget). Same
         edge-glow read at a fraction of the compositing cost, and it works on
         dpr>=3 with no FX-lite suppression. */
      .combo-aura {
        position: absolute;
        inset: 0;
        background: radial-gradient(125% 95% at 50% 50%, transparent 58%, ${COLORS.AMBER_WARN} 100%);
        opacity: calc(var(--combo-i, 0) * 0.5);
      }
      .combo-chip {
        position: absolute;
        top: 11%;
        left: 50%;
        transform: translateX(-50%);
        font-family: ${UI_FONT_STACK};
        font-size: ${withUiScale(36)};
        font-weight: 900;
        letter-spacing: 2px;
        color: ${COLORS.AMBER_WARN};
        text-shadow: 0 0 calc(8px + var(--combo-i, 0) * 22px) ${COLORS.AMBER_WARN};
        font-variant-numeric: tabular-nums;
      }
      .combo-chip.is-pulsing {
        animation: comboPulse 0.7s ${UI_EASE.glide} infinite;
      }
      @keyframes comboPulse {
        0%, 100% { transform: translateX(-50%) scale(1); }
        50% { transform: translateX(-50%) scale(1.09); }
      }
      /* Reduced-motion: freeze the pulse — the static "×N" + glow + aura still
         convey the live multiplier (information preserved). */
      @media (prefers-reduced-motion: reduce) {
        .combo-chip.is-pulsing { animation: none; }
        .combo-readout { transition: none; }
      }
    `;
    document.head.appendChild(style);
  }
}
